import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { formatMoney, tickerOfSymbol, type StressReport } from '@greencandle/api-client'
import { api } from '../../api'
import { Failed, pct } from '../primitives'

/**
 * QNT-2: named historical scenarios, replayed against the current book.
 *
 * <p>Behind a button for the same reason the optimiser is - see that panel's note.
 */

/**
 * UI-G2. Four historical crises, one selected at a time.
 *
 * <p>All four arrive in a single response, so switching between them is instant in the way the
 * story asks for - no request, no spinner, no chance of showing one scenario's headline beside
 * another's detail.
 *
 * <p>Like the optimiser, nothing runs on page load. A drawdown figure is a scenario under
 * stated assumptions, and it should appear because someone asked a question.
 */
export function StressPanel({ accountId }: { accountId: string }) {
  const [selected, setSelected] = useState<string | null>(null)

  const run = useMutation({
    mutationFn: () => api.stress(accountId),
    onSuccess: (report) => setSelected(report.scenarios[0]?.id ?? null),
  })

  const report: StressReport | undefined = run.data
  const scenario = report?.scenarios.find((s) => s.id === selected) ?? report?.scenarios[0]

  return (
    <section className="panel">
      <div className="panel-header">
        <h3>Stress test against historical crises</h3>
        <span className="panel-note">
          {report?.available
            ? `${report.observations} observations · computed in ${report.computeMillis?.toFixed(1)} ms`
            : 'QNT-2 · four scenarios'}
        </span>
      </div>

      <div className="controls-box">
        <div className="control-group control-end">
          <button
            type="button"
            className="btn-primary"
            disabled={run.isPending}
            onClick={() => run.mutate()}
          >
            {run.isPending ? 'Working…' : 'Run scenarios'}
          </button>
        </div>
        <div className="commit-note">
          Each scenario is a historical market shock applied to every position's beta. In a crisis
          correlations converge, so betas are pulled toward the average: diversification stops
          working exactly when it is needed.
        </div>
      </div>

      {run.isError && <Failed error={run.error} />}

      {report && !report.available && (
        <div className="panel-gap">
          <span className="badge badge-custom">not available</span>
          <p>{report.reason}</p>
        </div>
      )}

      {report?.available && scenario && (
        <>
          <div className="sub-nav stress-picker">
            {report.scenarios.map((s) => (
              <button
                key={s.id}
                type="button"
                aria-pressed={s.id === scenario.id}
                onClick={() => setSelected(s.id)}
              >
                {s.name}
              </button>
            ))}
          </div>

          <div className="risk-figures">
            <div className="risk-figure">
              <span className="label">Portfolio drawdown</span>
              <span className="value down">{pct(scenario.portfolioDelta)}</span>
            </div>
            <div className="risk-figure">
              <span className="label">In money</span>
              <span className="value down">
                {report.totalValue != null && scenario.portfolioDelta != null
                  ? formatMoney(
                      String(Number(report.totalValue) * scenario.portfolioDelta),
                      report.currency ?? 'USD',
                    )
                  : '—'}
              </span>
            </div>
            <div className="risk-figure">
              <span className="label">Market shock</span>
              <span className="value">{pct(scenario.factorShock)}</span>
            </div>
            <div className="risk-figure">
              <span className="label">Duration</span>
              <span className="value">
                {scenario.days} {scenario.days === 1 ? 'day' : 'days'}
              </span>
            </div>
          </div>

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Worst positions</th>
                  <th className="right">Shock</th>
                  <th className="right">Contribution</th>
                </tr>
              </thead>
              <tbody>
                {scenario.worst.map((holding) => (
                  <tr key={holding.symbol}>
                    <td>
                      <strong>{tickerOfSymbol(holding.symbol)}</strong>
                    </td>
                    <td className="right num down">{pct(holding.shock)}</td>
                    <td className="right num down">{pct(holding.contribution)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="panel-foot">{scenario.note}</p>
          <p className="panel-foot warn">
            The common factor explains {pct(report.factorShareOfVariance)} of the portfolio's movement. The market
            here is the first principal component of your own holdings, not an index: if the whole
            book is one sector, a 'market' shock is really a sector shock. A scenario under stated
            assumptions, not a forecast.
          </p>
        </>
      )}

      {!report && !run.isPending && (
        <p className="panel-foot">
          Black Monday 1987, the GFC, the COVID crash and the 2022 rate shock — against your
          current weights. Nothing computes on its own.
        </p>
      )}
    </section>
  )
}
